import { DropdownType, Popover, SvgArroIconOnlyTop } from '@ui-kit'
import { useState, useRef, useEffect } from 'react'

interface ActionsDropDownPropsType<T> {
  handleOpenOptions?: () => Promise<DropdownType<T>[]>
  initialOptions?: DropdownType<T>[]
  handleValueSelect?: (selectedOption: DropdownType<T>) => Promise<void>
  label?: string
  buttonCss?: string
  svgCss?: string
}
export const ActionsDropDown = <T,>({
  handleOpenOptions,
  initialOptions,
  handleValueSelect,
  label = 'Actions ',
  buttonCss = '',
  svgCss = '',
}: ActionsDropDownPropsType<T>) => {
  const [open, setOpen] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const [options, setOptions] = useState<DropdownType<T>[]>(
    initialOptions ?? []
  )
  const handleButtonKeyDown = async (e: React.KeyboardEvent) => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        await handleOpen()
        e.preventDefault()
      }
    }
  }

  const handleContentKeyDown = (e: React.KeyboardEvent) => {
    if (!open) return

    if (e.key === 'ArrowDown') {
      setHighlightedIndex((prev) => (prev + 1) % options.length)
      e.preventDefault()
    } else if (e.key === 'ArrowUp') {
      setHighlightedIndex(
        (prev) => (prev - 1 + options.length) % options.length
      )
      e.preventDefault()
    } else if (e.key === 'Enter') {
      if (highlightedIndex >= 0 && options[highlightedIndex]) {
        handleSelect(options[highlightedIndex])
      }
      e.preventDefault()
    } else if (e.key === 'Escape') {
      handleClose()
      buttonRef.current?.focus()
      e.preventDefault()
    } else if (e.key === 'Tab') {
      handleClose()
    }
  }
  const handleOpen = async () => {
    if (!open) {
      if (handleOpenOptions) {
        const newOptions = await handleOpenOptions()
        console.log(newOptions)

        setOptions(newOptions)
      } else {
        setOptions(initialOptions ?? [])
      }
      setHighlightedIndex(0)
    } else {
      setOptions([])
      setHighlightedIndex(-1)
    }
    setOpen((prev) => !prev)
  }
  const handleSelect = (option: DropdownType<T>) => {
    setOpen(false)
    setHighlightedIndex(-1)
    if (handleValueSelect) {
      handleValueSelect(option)
    }
  }
  const handleClose = () => {
    setOpen(false)
    setHighlightedIndex(-1)
    setOptions([])
  }
  useEffect(() => {
    if (open && contentRef.current) {
      contentRef.current.focus()
    }
  }, [open])

  return (
    <div className="relative inline-block">
      <button
        ref={buttonRef}
        onClick={handleOpen}
        onKeyDown={handleButtonKeyDown}
        className={`flex items-center gap-1 font-primary font-light text-xs rounded truncate border px-[0.625rem] cursor-pointer group h-button-height bg-accent-0 text-primary-900 border-primary-500 ${buttonCss}`}
      >
        {label}
        <span className="ml-1">
          <SvgArroIconOnlyTop
            color="black"
            className={`transform transition-transform duration-200 ${svgCss} ${
              open ? '' : 'rotate-180'
            }`}
          />
        </span>
      </button>

      {open && options?.length > 0 && (
        <Popover
          isOpen={open}
          showCloseButton={false}
          buttonRef={buttonRef}
          cssClassName="m-0 !p-0 w-[10.5rem] overflow-hidden "
          contentClassName="w-[calc(10.5rem - 2px)]  focus-within:outline focus-within:outline-2 focus-within:outline-blue-500 rounded-[inherit] "
          closeOnOutsideClick={true}
          onClose={handleClose}
          content={
            <div
              className="flex flex-col bg-primary-100 gap-[.1rem] "
              onKeyDown={handleContentKeyDown}
              tabIndex={-1}
              role="listbox"
              aria-label="Actions options"
              ref={contentRef}
            >
              {options.map((option, index) => (
                <OptionContainer
                  handleSelect={handleSelect}
                  highlightedIndex={highlightedIndex}
                  index={index}
                  option={option}
                  setHighlightedIndex={setHighlightedIndex}
                  key={option?.label}
                />
              ))}
            </div>
          }
        ></Popover>
      )}
    </div>
  )
}
interface OptionContainerPropsType<T> {
  highlightedIndex: number
  handleSelect: (option: DropdownType<T>) => void
  option: DropdownType<T>
  setHighlightedIndex: React.Dispatch<React.SetStateAction<number>>
  index: number
}
const OptionContainer = <T,>({
  highlightedIndex,
  handleSelect,
  option,
  setHighlightedIndex,
  index,
}: OptionContainerPropsType<T>) => {
  const isHighlighted = highlightedIndex === index
  return (
    <button
      key={option?.label}
      onClick={() => handleSelect(option)}
      onMouseEnter={() => setHighlightedIndex(index)}
      className={`w-full text-left px-4 py-2 text-sm focus:outline-none ${
        isHighlighted ? 'bg-secondary-100' : 'bg-white hover:bg-secondary-100'
      }`}
      role="menuitem"
    >
      {option?.label}
    </button>
  )
}
